import React, { createContext, useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import { useFormik } from "formik";
import { userInfoSessionStoreage } from "../utils/userInfoSessionStoreage";

import { login, getUserInfo, updateInstances, getUsers } from "../api";
import { authTokenPayload } from "../config/constant";

const AuthContext = createContext({
  userInfo: {},
  setUserInfo: () => {},
  authenticateUser: () => {},
  refreshUserInfo: async () => {},
  refreshOnlyUserInfo: async () => {},
  refreshSubscriptions: async () => {},
});

export default function AuthProvider({ children }) {
  const [userInfo, setUserInfo] = useState({});
  const [snackBar, setSnackBar] = useState({
    open: false,
    msg: "",
    severity: "",
  });
  useEffect(() => {
    try {
      setUserInfo(userInfoSessionStoreage.get());
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (Object.keys(userInfo).length === 0) return;
    userInfoSessionStoreage.set(userInfo);
  }, [userInfo]);

  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);

  async function refreshUserInfo(userId, apiKey, service, amp) {
    try {
      const { userid = userId, apikey = apiKey } = userInfo;
      if (service) {
        sessionStorage.setItem("userservice", service);
      }
      if (amp) {
        sessionStorage.setItem("amp", amp);
      }

      const freshUserInfo = await getUserInfo(userid, apikey, service);

      if (freshUserInfo?.apikey) {
        sessionStorage.setItem("userInfo", JSON.stringify(freshUserInfo || []));
      }

      if (typeof freshUserInfo?.server_details?.[0] === "object") {
        updateInstances(freshUserInfo.server_details, apikey);
      }

      let bangdb_account_id;

      if (freshUserInfo?.parent_user_account) {
        bangdb_account_id = freshUserInfo?.parent_user_account;
      } else {
        bangdb_account_id = freshUserInfo?.user_account;
      }

      const allSubscriptions = await getUsers(
        authTokenPayload({
          user_account: bangdb_account_id,
          op: 1,
        })
      );

      sessionStorage.setItem(
        "subscriptions",
        JSON.stringify(allSubscriptions?.subscription_details || [])
      );

      setUserInfo({ ...freshUserInfo, apikey });

      return { ...freshUserInfo, apikey };
    } catch (error) {
      console.log(error);
    }
  }

  async function refreshOnlyUserInfo(userId, apiKey, service, amp) {
    try {
      const { userid = userId, apikey = apiKey } = userInfo;
      if (service) {
        sessionStorage.setItem("userservice", service);
      }
      if (amp) {
        sessionStorage.setItem("amp", amp);
      }
      const freshUserInfo = await getUserInfo(userid, apikey, service);

      if (typeof freshUserInfo?.server_details?.[0] === "object") {
        updateInstances(freshUserInfo.server_details, apikey);
      }

      setUserInfo({ ...freshUserInfo, apikey });

      return { ...freshUserInfo, apikey };
    } catch (error) {
      console.log(error);
    }
  }

  async function refreshSubscriptions(userId, apiKey, service) {
    try {
      const userDetails = sessionStorage.getItem("userInfo")
        ? JSON.parse(sessionStorage.getItem("userInfo"))
        : null;

      if (userDetails) {
        let bangdb_account_id;

        if (userDetails?.parent_user_account) {
          bangdb_account_id = userDetails?.parent_user_account;
        } else {
          bangdb_account_id = userDetails?.user_account;
        }

        const allSubscriptions = await getUsers(
          authTokenPayload({
            user_account: bangdb_account_id,
            op: 1,
          })
        );

        sessionStorage.setItem(
          "subscriptions",
          JSON.stringify(allSubscriptions?.subscription_details || [])
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  function handleLogout() {
    setUserInfo({});
    userInfoSessionStoreage.set({});
    sessionStorage.clear();
    localStorage.clear();
    window.location.assign("/auth/login");
  }

  const formik = useFormik({
    initialValues: {
      userid: "",
      password: "",
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);
        const { userid, password } = values;
        const apikey = await login(userid, password).then((data) => {
          const { errcode, apikey } = data;
          if (errcode === 0) {
            setIsLoginDialogOpen(false);

            return apikey;
          } else {
            setSnackBar({
              open: true,
              severity: "error",
              msg: "Authentication Failed.",
            });
          }
        });

        if (!apikey) {
          throw new Error("Login failed");
        }
        const userInfo = await getUserInfo(userid, apikey);

        let bangdb_account_id;

        if (userInfo?.parent_user_account) {
          bangdb_account_id = userInfo?.parent_user_account;
        } else {
          bangdb_account_id = userInfo?.user_account;
        }

        const allSubscriptions = await getUsers(
          authTokenPayload({
            user_account: bangdb_account_id,
            op: 1,
          })
        );

        if (typeof userInfo?.server_details?.[0] === "object") {
          updateInstances(userInfo.server_details, apikey);
        }

        sessionStorage.setItem(
          "subscriptions",
          JSON.stringify(allSubscriptions?.subscription_details || [])
        );
        setUserInfo({ ...userInfo, apikey });
        window.location.reload();
        resetForm();
      } catch (error) {
        console.log(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        setUserInfo,
        authenticateUser: () => setIsLoginDialogOpen(true),
        refreshUserInfo,
        refreshOnlyUserInfo,
        refreshSubscriptions,
        logout: handleLogout,
      }}
    >
      {children}
      <Snackbar
        open={snackBar.open}
        autoHideDuration={6000}
        onClose={() => setSnackBar({ open: false, msg: "", severity: "" })}
      >
        <Alert
          onClose={() => setSnackBar({ open: false, msg: "", severity: "" })}
          severity={snackBar.severity}
          sx={{ width: "100%" }}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>
      <Dialog
        maxWidth="xs"
        open={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
      >
        <DialogTitle>Login</DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="User Id"
                  variant="outlined"
                  {...formik.getFieldProps("userid")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type="password"
                  autoComplete="current-password"
                  {...formik.getFieldProps("password")}
                />
              </Grid>
              <Grid container item xs={12} justifyContent="flex-end">
                <Button
                  disabled={formik.isSubmitting}
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Login
                </Button>
              </Grid>
            </Grid>
          </form>
          <Divider>OR</Divider>
          <Typography
            variant="body2"
            style={{
              textAlign: "center",
              padding: "1rem",
            }}
          >
            {/* create an account */}
            <Typography variant="h6">
              <a
                href={`https://${
                  window.location.host || "appstore.bangdb.com"
                }/`}
                target="_blank"
                rel="noreferrer"
              >
                Create an account
              </a>
            </Typography>
          </Typography>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
